import React, { useState } from 'react';
import { ScrollView, View } from 'react-native';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { currentPeriod, inr, P, periodLabel, type ID, type PayrollInput } from '@hrms/shared';
import { useAuth } from '../../../auth/AuthProvider';
import { api } from '../../../lib/api';
import { errorMessage } from '../../../lib/errors';
import { useBreakpoint, useDebounced } from '../../../lib/hooks';
import { useMut } from '../../../lib/query';
import { useParams } from '../../../navigation/nav';
import { Alert, Badge, Button, Card, Checkbox, EmptyState, IconButton, Loading, Modal, PageHeader, Row, Screen, Stack, Text, TextField, useTheme, useToast } from '../../../ui';
import { NumField, PeriodSelect } from '../components/common';
import { EmployeePicker, PersonCell } from '../components/EmployeePicker';

type Line = { code: string; name: string; amount: number; taxable?: boolean };
type Edit = Partial<Pick<PayrollInput, 'lop_days' | 'arrears' | 'ot_hours' | 'remarks' | 'one_time_pay' | 'one_time_deductions'>>;
type LinesKind = 'one_time_pay' | 'one_time_deductions';

function LinesModal({ title, lines, taxable, onSave, onClose }: { title: string; lines: Line[]; taxable?: boolean; onSave: (l: Line[]) => void; onClose: () => void }) {
  const [l, setL] = useState<Line[]>(lines);
  const upd = (i: number, patch: Partial<Line>) => setL(l.map((y, j) => (j === i ? { ...y, ...patch } : y)));
  const total = l.reduce((a, x) => a + (x.amount || 0), 0);
  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      subtitle={`Total ${inr(total)}`}
      width={640}
      testID="lines-modal"
      footer={
        <>
          <Button title="Cancel" onPress={onClose} />
          <Button variant="primary" title="Apply" onPress={() => onSave(l.filter((x) => x.amount && x.code))} testID="lines-apply" />
        </>
      }
    >
      {!l.length && <Text variant="muted">No lines yet.</Text>}
      {l.map((x, i) => (
        <Row key={i} gap={8} align="flex-end">
          <TextField label={i === 0 ? 'Code' : undefined} accessibilityLabel="Code" placeholder="BONUS" autoCapitalize="characters" value={x.code} onChangeText={(v) => upd(i, { code: v.toUpperCase().replace(/[^A-Z0-9_]/g, '') })} containerStyle={{ width: 110 }} />
          <TextField label={i === 0 ? 'Name' : undefined} accessibilityLabel="Name" placeholder="Performance bonus" value={x.name} onChangeText={(v) => upd(i, { name: v })} containerStyle={{ flex: 1, minWidth: 160 }} />
          <NumField label={i === 0 ? 'Amount ₹' : undefined} accessibilityLabel="Amount" value={x.amount} onChange={(v) => upd(i, { amount: v ?? 0 })} width={120} />
          {taxable && (
            <View style={{ height: 40, justifyContent: 'center' }}>
              <Checkbox label="Taxable" value={x.taxable !== false} onChange={(v) => upd(i, { taxable: v })} />
            </View>
          )}
          <IconButton icon="x" label="Remove line" onPress={() => setL(l.filter((_, j) => j !== i))} />
        </Row>
      ))}
      <Button icon="plus" title="Add line" onPress={() => setL([...l, { code: '', name: '', amount: 0, ...(taxable ? { taxable: true } : {}) }])} testID="lines-add" />
    </Modal>
  );
}

const COLS = [
  { key: 'employee', title: 'Employee', w: 180 },
  { key: 'source', title: 'Source', w: 96 },
  { key: 'lop', title: 'LOP days', w: 96 },
  { key: 'arrears', title: 'Arrears ₹', w: 116 },
  { key: 'ot', title: 'OT hours', w: 96 },
  { key: 'otp', title: 'One-time pay', w: 130 },
  { key: 'otd', title: 'One-time deductions', w: 150 },
  { key: 'remarks', title: 'Remarks', w: 160 },
  { key: 'save', title: '', w: 90 },
];

export function PayrollInputsScreen() {
  const params = useParams<{ period: string }>();
  const { can } = useAuth();
  const manage = can(P.PAYROLL_MANAGE);
  const { colors } = useTheme();
  const { isPhone } = useBreakpoint();
  const qc = useQueryClient();
  const toast = useToast();
  const [period, setPeriod] = useState(params.period && /^\d{4}-\d{2}$/.test(params.period) ? params.period : currentPeriod());
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const ds = useDebounced(search);
  const q = useQuery({ queryKey: ['payroll', 'inputs', period, ds, page], queryFn: () => api.payroll.inputs(period, { search: ds || undefined, page, per_page: 50 }) });
  const [edits, setEdits] = useState<Record<ID, Edit>>({});
  const [modal, setModal] = useState<{ row: PayrollInput; kind: LinesKind } | null>(null);
  const [addEmp, setAddEmp] = useState<ID | null>(null);
  const [savingAll, setSavingAll] = useState(false);
  const [rowErr, setRowErr] = useState<Record<ID, string>>({});

  const body = (r: PayrollInput) => ({ ...r, ...edits[r.employee_id], employee_id: r.employee_id, period });
  const clearEdit = (empId: ID) =>
    setEdits((e) => {
      const n = { ...e };
      delete n[empId];
      return n;
    });
  const saveRow = useMut((r: PayrollInput) => api.payroll.saveInput(body(r)), {
    invalidate: [['payroll', 'inputs']],
    success: (_x, r) => `Saved ${r.employee?.full_name ?? 'row'}`,
    onSuccess: (_x, r) => {
      clearEdit(r.employee_id);
      setRowErr((e) => ({ ...e, [r.employee_id]: '' }));
    },
  });
  const add = useMut(() => api.payroll.saveInput({ employee_id: addEmp!, period, lop_days: 0, arrears: 0, ot_hours: 0, one_time_pay: [], one_time_deductions: [] }), {
    invalidate: [['payroll', 'inputs']],
    success: 'Input row added',
    onSuccess: () => setAddEmp(null),
  });

  const list = q.data?.data ?? [];
  const dirty = Object.keys(edits).length;
  const val = <K extends keyof Edit>(r: PayrollInput, k: K) => (edits[r.employee_id]?.[k] ?? r[k]) as PayrollInput[K];
  const setVal = (r: PayrollInput, patch: Edit) => setEdits((e) => ({ ...e, [r.employee_id]: { ...e[r.employee_id], ...patch } }));
  const sum = (l: { amount: number }[] | undefined) => (l ?? []).reduce((a, x) => a + x.amount, 0);

  const saveAll = async () => {
    setSavingAll(true);
    let ok = 0;
    const errs: Record<ID, string> = {};
    for (const r of list.filter((x) => edits[x.employee_id])) {
      try {
        await api.payroll.saveInput(body(r));
        ok++;
        clearEdit(r.employee_id);
      } catch (e) {
        errs[r.employee_id] = errorMessage(e);
      }
    }
    setSavingAll(false);
    setRowErr(errs);
    void qc.invalidateQueries({ queryKey: ['payroll', 'inputs'] });
    const failed = Object.keys(errs).length;
    if (failed) toast.error(`${ok} saved, ${failed} failed`);
    else toast.success(`${ok} row${ok === 1 ? '' : 's'} saved`);
  };

  const changePeriod = (p: string) => {
    setPeriod(p);
    setEdits({});
    setPage(1);
  };

  const numCell = (r: PayrollInput, k: 'lop_days' | 'arrears' | 'ot_hours', label: string, width: number, withLabel?: boolean) => (
    <NumField
      label={withLabel ? label : undefined}
      accessibilityLabel={`${label} for ${r.employee?.full_name ?? r.employee_id}`}
      disabled={!manage}
      width={width}
      value={val(r, k) ?? 0}
      onChange={(v) => setVal(r, { [k]: v ?? 0 })}
      onSubmitEditing={() => edits[r.employee_id] && saveRow.mutate(r)}
      style={{ textAlign: 'right' }}
    />
  );
  const linesBtn = (r: PayrollInput, kind: LinesKind) => (
    <Button size="sm" disabled={!manage} title={`${inr(sum(val(r, kind)))} · ${val(r, kind)?.length ?? 0}`} onPress={() => setModal({ row: r, kind })} accessibilityLabel={`${kind === 'one_time_pay' ? 'One-time pay' : 'One-time deductions'} for ${r.employee?.full_name ?? ''}`} />
  );
  const saveBtn = (r: PayrollInput) =>
    edits[r.employee_id] ? <Button size="sm" variant="primary" title="Save" loading={saveRow.isPending && saveRow.variables?.employee_id === r.employee_id} onPress={() => saveRow.mutate(r)} /> : null;

  return (
    <Screen refreshing={q.isRefetching} onRefresh={() => q.refetch()} maxWidth={1400} testID="payroll-inputs">
      <PageHeader
        title="Payroll inputs"
        subtitle={`Variable inputs for ${periodLabel(period)}. Rows marked "attendance" come from the attendance lock.`}
        actions={manage ? <Button variant="primary" icon="check" title={`Save ${dirty || ''} change${dirty === 1 ? '' : 's'}`} disabled={!dirty} loading={savingAll} onPress={() => void saveAll()} testID="save-all" /> : undefined}
      />
      <Card>
        <Row gap={12} align="flex-end">
          <View style={{ width: 200 }}>
            <PeriodSelect label="Period" value={period} onChange={changePeriod} />
          </View>
          <TextField
            label="Search"
            placeholder="Employee name or code…"
            value={search}
            onChangeText={(v) => {
              setSearch(v);
              setPage(1);
            }}
            containerStyle={{ flex: 1, minWidth: 180 }}
          />
          {manage && (
            <>
              <View style={{ flex: 1, minWidth: 220 }}>
                <EmployeePicker label="Add employee row" value={addEmp} onChange={(v) => setAddEmp(v)} placeholder="Pick employee…" status="active" />
              </View>
              <Button icon="plus" title="Add" style={{ alignSelf: 'flex-end' }} disabled={!addEmp} loading={add.isPending} onPress={() => add.mutate(undefined)} testID="add-input" />
            </>
          )}
        </Row>
      </Card>
      {dirty > 0 && <Alert tone="warning">{`${dirty} unsaved row${dirty === 1 ? '' : 's'} (highlighted). Save before processing payroll.`}</Alert>}

      {q.isLoading ? (
        <Loading />
      ) : !list.length ? (
        <Card>
          <EmptyState icon="grid" title="No inputs for this period" description="Lock attendance to auto-generate LOP inputs, or add employee rows manually." />
        </Card>
      ) : isPhone ? (
        <Stack gap={10} testID="inputs-cards">
          {list.map((r) => (
            <Card key={r.employee_id} style={edits[r.employee_id] ? { borderColor: colors.warning, backgroundColor: colors.warningSoft } : undefined}>
              <Stack gap={10}>
                <Row justify="space-between">
                  <PersonCell emp={r.employee} fallback={r.employee_id} />
                  <Badge tone={r.source === 'manual' ? 'neutral' : 'info'}>{r.source}</Badge>
                </Row>
                <Row gap={8}>
                  {numCell(r, 'lop_days', 'LOP days', 96, true)}
                  {numCell(r, 'arrears', 'Arrears ₹', 110, true)}
                  {numCell(r, 'ot_hours', 'OT hours', 96, true)}
                </Row>
                <Row gap={8}>
                  <View style={{ gap: 4 }}>
                    <Text variant="small">One-time pay</Text>
                    {linesBtn(r, 'one_time_pay')}
                  </View>
                  <View style={{ gap: 4 }}>
                    <Text variant="small">One-time deductions</Text>
                    {linesBtn(r, 'one_time_deductions')}
                  </View>
                </Row>
                <TextField label="Remarks" disabled={!manage} value={val(r, 'remarks') ?? ''} onChangeText={(v) => setVal(r, { remarks: v })} />
                {!!rowErr[r.employee_id] && <Text size={12} color={colors.danger}>{rowErr[r.employee_id]}</Text>}
                {saveBtn(r)}
              </Stack>
            </Card>
          ))}
        </Stack>
      ) : (
        <View style={{ backgroundColor: colors.surface, borderRadius: 12, borderWidth: 1, borderColor: colors.border, overflow: 'hidden' }} testID="inputs-grid">
          <ScrollView horizontal showsHorizontalScrollIndicator>
            <View style={{ minWidth: '100%' }}>
              <View style={{ flexDirection: 'row', backgroundColor: colors.surface2, borderBottomWidth: 1, borderBottomColor: colors.border, minHeight: 36, alignItems: 'center' }}>
                {COLS.map((c) => (
                  <View key={c.key} style={{ width: c.w, paddingHorizontal: 8, flexGrow: c.key === 'employee' ? 1 : 0 }}>
                    <Text variant="caption">{c.title}</Text>
                  </View>
                ))}
              </View>
              {list.map((r, i) => (
                <View key={r.employee_id} style={{ borderBottomWidth: i < list.length - 1 ? 1 : 0, borderBottomColor: colors.border, backgroundColor: edits[r.employee_id] ? colors.warningSoft : 'transparent' }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', minHeight: 52, paddingVertical: 6 }}>
                    <View style={{ width: COLS[0].w, paddingHorizontal: 8, flexGrow: 1 }}>
                      <PersonCell emp={r.employee} fallback={r.employee_id} />
                    </View>
                    <View style={{ width: COLS[1].w, paddingHorizontal: 8 }}>
                      <Badge tone={r.source === 'manual' ? 'neutral' : 'info'}>{r.source}</Badge>
                    </View>
                    <View style={{ width: COLS[2].w, paddingHorizontal: 8 }}>{numCell(r, 'lop_days', 'LOP days', COLS[2].w - 16)}</View>
                    <View style={{ width: COLS[3].w, paddingHorizontal: 8 }}>{numCell(r, 'arrears', 'Arrears', COLS[3].w - 16)}</View>
                    <View style={{ width: COLS[4].w, paddingHorizontal: 8 }}>{numCell(r, 'ot_hours', 'OT hours', COLS[4].w - 16)}</View>
                    <View style={{ width: COLS[5].w, paddingHorizontal: 8 }}>{linesBtn(r, 'one_time_pay')}</View>
                    <View style={{ width: COLS[6].w, paddingHorizontal: 8 }}>{linesBtn(r, 'one_time_deductions')}</View>
                    <View style={{ width: COLS[7].w, paddingHorizontal: 8 }}>
                      <TextField
                        accessibilityLabel={`Remarks for ${r.employee?.full_name ?? r.employee_id}`}
                        disabled={!manage}
                        value={val(r, 'remarks') ?? ''}
                        onChangeText={(v) => setVal(r, { remarks: v })}
                        onSubmitEditing={() => edits[r.employee_id] && saveRow.mutate(r)}
                      />
                    </View>
                    <View style={{ width: COLS[8].w, paddingHorizontal: 8 }}>{saveBtn(r)}</View>
                  </View>
                  {!!rowErr[r.employee_id] && (
                    <Text size={12} color={colors.danger} style={{ paddingHorizontal: 8, paddingBottom: 6 }}>
                      {rowErr[r.employee_id]}
                    </Text>
                  )}
                </View>
              ))}
            </View>
          </ScrollView>
        </View>
      )}
      {q.data && q.data.last_page > 1 && (
        <Row justify="flex-end">
          <Button size="sm" icon="chevronLeft" title="Prev" disabled={page <= 1} onPress={() => setPage(page - 1)} />
          <Text variant="small">{`Page ${q.data.current_page} of ${q.data.last_page}`}</Text>
          <Button size="sm" iconRight="chevronRight" title="Next" disabled={page >= q.data.last_page} onPress={() => setPage(page + 1)} />
        </Row>
      )}
      {!isPhone && manage && <Text variant="small">Tip: press Enter in a cell to save that row. Unsaved rows are highlighted.</Text>}
      {modal && (
        <LinesModal
          title={`${modal.kind === 'one_time_pay' ? 'One-time pay' : 'One-time deductions'} · ${modal.row.employee?.full_name ?? ''}`}
          lines={((val(modal.row, modal.kind) as Line[]) ?? []).map((x) => ({ ...x }))}
          taxable={modal.kind === 'one_time_pay'}
          onClose={() => setModal(null)}
          onSave={(l) => {
            setVal(modal.row, { [modal.kind]: l });
            setModal(null);
          }}
        />
      )}
    </Screen>
  );
}
